import { type Ref, useLayoutEffect, useRef, useState } from 'react'

import { FloatingAnchored, FloatingBox, FloatingModal } from '@/components/common/Floating'
import { useBreakpoints } from '@/components/providers/BreakpointProvider'
import { type IconType } from '@/defs/icon'
import {
  type InputProps,
  type TypeaheadOptionGroupType,
  type TypeaheadOptionType,
  type TypeaheadSelectionType,
} from '@/defs/input'
import { useModalProps } from '@/hooks/useModalProps'
import { cn } from '@/utils/commonUtil'

import styles from './InputTypeaheadContent.module.scss'
import { InputTypeaheadContentField } from './InputTypeaheadContentField/InputTypeaheadContentField'
import { InputTypeaheadContentList } from './InputTypeaheadContentList/InputTypeaheadContentList'
import { useTypeaheadContent } from './useTypeaheadContent'

type PropsShared<T extends string = string> = {
  'aria-label'?: string
  autoFocus?: boolean
  disabled?: boolean
  icon?: IconType
  loading?: boolean
  minCharacters?: number
  onClear?: () => void
  onSearch?: (search: string) => void
  optionGroups?: TypeaheadOptionGroupType<T>[]
  optionReset?: TypeaheadOptionType<T>
  options?: TypeaheadOptionType<T>[]
  placeholder?: string
  ref?: Ref<HTMLInputElement>
  required?: boolean
  showClear?: boolean
  showLoadingMask?: boolean
}

type PropsSingle<T extends string = string> = {
  multiselect?: false
  onChange?: never
  onSelect?: (selected: T, label?: string) => void
  value?: T
}

type PropsMultiselect<T extends string = string> = {
  multiselect: true
  onChange: (selections: TypeaheadSelectionType<T>[]) => void
  onSelect?: never
  value: T[]
}

export type BaseProps<T extends string = string> = PropsShared<T> & (PropsSingle<T> | PropsMultiselect<T>)

export type Props<T extends string = string> = BaseProps<T> & InputProps

const InputTypeaheadContentDesktop = <T extends string = string>(props: Props<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const refContainer = useRef<HTMLDivElement>(null)
  const typeahead = useTypeaheadContent<T>({ ...props, closeOnSelect: true, isOpen, setIsOpen })
  const { hasMinCharacters, inputProps, isEmpty, multiselect } = typeahead
  const isListOpen = isOpen && hasMinCharacters

  return (
    <div
      className={cn(styles.container, { isEmpty, isListOpen, multiselect, ...inputProps, styles })}
      ref={refContainer}
    >
      <FloatingAnchored
        content={<InputTypeaheadContentList {...typeahead} />}
        isOpen={isListOpen}
        onInteractOutside={(e) => {
          const target = e.detail.originalEvent.target
          if (target instanceof Node && refContainer.current?.contains(target)) e.preventDefault()
        }}
        setIsOpen={setIsOpen}
        width='full'
      />
      <InputTypeaheadContentField {...typeahead} />
    </div>
  )
}

const InputTypeaheadContentMobile = <T extends string = string>(props: Props<T>) => {
  const { isFirst, isOpen, isTop, setIsOpen } = useModalProps()
  const typeahead = useTypeaheadContent<T>({ ...props, isOpen, setIsOpen })
  const { autoFocus, hasMinCharacters, hasResults, inputProps, isEmpty, multiselect } = typeahead

  useLayoutEffect(() => {
    if (autoFocus) setIsOpen(true)
  }, [autoFocus, setIsOpen])

  const className = cn(styles.container, { isEmpty, multiselect, styles, ...inputProps })
  const results = hasResults || hasMinCharacters ? <InputTypeaheadContentList {...typeahead} /> : null

  return (
    <>
      <FloatingModal
        content={
          <div className={styles.modal}>
            <div className={styles.modalContent}>
              <div className={className}>
                <InputTypeaheadContentField {...typeahead} autoFocus />
              </div>
            </div>
            {results && <FloatingBox content={results} width='full' />}
          </div>
        }
        disablePointerEvents
        isFirst={isFirst}
        isOpen={isOpen}
        isTop={isTop}
        setIsOpen={setIsOpen}
        variant='nobox'
        width='full'
      />
      <div className={className}>
        <InputTypeaheadContentField {...typeahead} variant='trigger' />
      </div>
    </>
  )
}

export const InputTypeaheadContent = <T extends string = string>(props: Props<T>) => {
  const { isDesktop } = useBreakpoints()

  return isDesktop ? <InputTypeaheadContentDesktop {...props} /> : <InputTypeaheadContentMobile {...props} />
}
